import React, { useState } from "react";
import { Button, Col, Form, Modal, Row } from "react-bootstrap";
import { useMask } from "@react-input/mask";
import { connect } from "react-redux";
import { confirmAlert } from "react-confirm-alert";
import "react-confirm-alert/src/react-confirm-alert.css";
import { toast } from "react-toastify";
import CustomDataTable from "../../layouts/DataTables/CustomDataTable";
import { VzPageContainer } from "../../layouts/VzPageContainer";
import {
  getUserDataCorrection,
  updateUserDataCorrection,
} from "./UserDataCorrectionAction";

const UserDataCorrection = ({
  getUserDataCorrection,
  updateUserDataCorrection,
}) => {
  const [cnic, setCnic] = useState("");
  const [vizShops, setVizShops] = useState([]);
  const [users, setUsers] = useState([]);
  const [selectedUserId, setSelectedUserId] = useState(null);
  const [hasSearched, setHasSearched] = useState(false);
  const [isSearching, setIsSearching] = useState(false);
  const [isUpdating, setIsUpdating] = useState(false);

  const cnicInputRef = useMask({
    mask: "_____-_______-_",
    replacement: { _: /\d/ },
  });

  const toggleUser = (id) => {
    setSelectedUserId((currentId) => (currentId === id ? null : id));
  };

  const vizShopColumns = [
    {
      Header: "VizShop Code",
      accessor: "vizShopCode",
      disableSortBy: true,
    },
    {
      Header: "VizShop Name",
      accessor: "vizShopName",
      disableSortBy: true,
    },
    {
      Header: "CNIC",
      accessor: "cnic",
      disableSortBy: true,
    },
    {
      Header: "Contact Numbers",
      accessor: "contactNumbers",
      disableSortBy: true,
    },
  ];

  const userColumns = [
    {
      Header: () => <div className="text-center">Select</div>,
      id: "select",
      disableSortBy: true,
      Cell: ({ row: { original } }) => (
        <div className="d-flex justify-content-center">
          <Form.Check
            type="checkbox"
            aria-label={`Select ${original.userName}`}
            checked={selectedUserId === original.id}
            onChange={() => toggleUser(original.id)}
          />
        </div>
      ),
    },
    {
      Header: "User Name",
      accessor: "userName",
      disableSortBy: true,
    },
    {
      Header: "Full Name",
      accessor: "fullName",
      disableSortBy: true,
    },
    {
      Header: "Contact Number",
      accessor: "contactNumber",
      disableSortBy: true,
    },
    {
      Header: "CNIC",
      accessor: "cnic",
      disableSortBy: true,
    },
  ];

  const loadUserData = async (normalizedCnic) => {
    setIsSearching(true);
    setHasSearched(false);
    setSelectedUserId(null);

    try {
      const result = await getUserDataCorrection(normalizedCnic);
      const fetchedVizShops = result?.vizShops ?? [];
      setVizShops(fetchedVizShops);
      setUsers(result?.users ?? []);
      setHasSearched(true);
    } catch (error) {
      setVizShops([]);
      setUsers([]);
      toast.error(
        error.response?.data?.message ?? "Unable to fetch user data",
      );
    } finally {
      setIsSearching(false);
    }
  };

  const searchByCnic = async (event) => {
    event.preventDefault();

    const normalizedCnic = cnic.replace(/\D/g, "");
    if (normalizedCnic.length !== 13) {
      toast.error("Please enter a valid 13-digit CNIC");
      return;
    }

    await loadUserData(normalizedCnic);
  };

  const updateSelectedData = async () => {
    setIsUpdating(true);

    try {
      const result = await updateUserDataCorrection({
        vizShops,
        users: users.map((user) => ({
          ...user,
          isSelected: selectedUserId === user.id,
        })),
      });
      toast.success(result?.message ?? "User data updated successfully");
      await loadUserData(cnic.replace(/\D/g, ""));
    } catch (error) {
      toast.error(
        error.response?.data?.message ?? "Unable to update user data",
      );
    } finally {
      setIsUpdating(false);
    }
  };

  const confirmUpdate = () => {
    if (vizShops.length === 0) {
      toast.error("No VizShop is available to update");
      return;
    }

    if (!selectedUserId) {
      toast.error("Please select a user");
      return;
    }

    confirmAlert({
      closeOnEscape: false,
      closeOnClickOutside: false,
      customUI: ({ onClose }) => (
        <div className="custom-ui">
          <Modal.Dialog>
            <Modal.Header id="customized-dialog-title">
              <Modal.Title>Confirm Update</Modal.Title>
              <button
                type="button"
                className="btn-close"
                aria-label="No"
                onClick={onClose}
              />
            </Modal.Header>
            <Modal.Body>
              <p>Are you sure you want to update the selected user data?</p>
            </Modal.Body>
            <Modal.Footer>
              <Button
                variant="outline-primary"
                onClick={() => {
                  updateSelectedData();
                  onClose();
                }}
              >
                Yes, Do it!
              </Button>
              <Button variant="primary" onClick={onClose}>
                No
              </Button>
            </Modal.Footer>
          </Modal.Dialog>
        </div>
      ),
    });
  };

  return (
    <VzPageContainer title="Settings > User Data Correction">
      <Row>
        <Col xs={12}>
          <h4>
            <b>User Data Correction</b>
          </h4>
        </Col>
      </Row>

      <Form onSubmit={searchByCnic} className="mb-4">
        <Row className="align-items-end">
          <Col xs={12} md={4}>
            <Form.Group controlId="userDataCorrectionCnic">
              <Form.Label>User CNIC</Form.Label>
              <Form.Control
                ref={cnicInputRef}
                value={cnic}
                onChange={(event) => setCnic(event.target.value)}
                placeholder="12345-1234567-1"
                autoComplete="off"
              />
            </Form.Group>
          </Col>
          <Col xs={12} md="auto" className="mt-3 mt-md-0">
            <Button type="submit" variant="primary" disabled={isSearching}>
              {isSearching ? "Searching..." : "Search"}
            </Button>
          </Col>
        </Row>
      </Form>

      {hasSearched && (
        <>
          <Row>
            <Col xs={12}>
              <h5 className="fw-bold text-orange">Viz Shops</h5>
              <CustomDataTable
                data={vizShops}
                columns={vizShopColumns}
                getTrProps={() => null}
                IsPagingEnable={false}
                IsSearchEnable={false}
                minHeight="auto"
              />
            </Col>
          </Row>

          <Row className="mt-2">
            <Col xs={12}>
              <h5 className="fw-bold text-orange">Users</h5>
              <CustomDataTable
                data={users}
                columns={userColumns}
                getTrProps={() => null}
                IsPagingEnable={false}
                IsSearchEnable={false}
                minHeight="auto"
              />
            </Col>
          </Row>

          <Row className="mt-3">
            <Col xs={12} className="text-end">
              <Button
                type="button"
                variant="primary"
                onClick={confirmUpdate}
                disabled={isUpdating}
              >
                {isUpdating ? "Updating..." : "Update"}
              </Button>
            </Col>
          </Row>
        </>
      )}
    </VzPageContainer>
  );
};

export default connect(null, {
  getUserDataCorrection,
  updateUserDataCorrection,
})(UserDataCorrection);
